import { useBlocker, useRouter } from "@tanstack/solid-router";
import { onCleanup } from "solid-js";

/**
 * Back-button-closes-overlay (premium PWA/mobile pattern).
 *
 * When an overlay (dialog/sheet/command-palette/unrouted drawer) is open
 * and the user presses the browser's "back", we want to CLOSE the overlay instead of
 * navigating through history.
 *
 * IMPORTANT — why we do NOT manipulate history by hand: TanStack Router patches
 * the History API (the `window.history` instance) and, besides maintaining an
 * index/key per entry, calls `router.load()` on every new entry. Pushing
 * a manual entry:
 *   - if it duplicates the router index → `delta = 0` on popstate → loop → HANG;
 *   - if it increments it → the router treats it as real navigation, reloads the route and
 *     REMOUNTS the component, resetting the overlay's local signal → the overlay DOES NOT OPEN.
 * That's why we use the router's own mechanism: `useBlocker`. We do not touch
 * history on open (the overlay opens normally); we only intercept the "BACK" action:
 * close the top overlay and cancel navigation (the router does `go(1)`).
 *
 * LIFO: nested overlays register their entry; "back" closes the most recent
 * one still open. It does not interfere with real navigation (PUSH/REPLACE/FORWARD pass).
 *
 * ── CC-19: why closing is DEFERRED (one macrotask later) ──
 * When the blocker decides to block BACK, the router library runs,
 * SYNCHRONOUSLY after our `shouldBlockFn`, `history.go(1)` to restore the
 * entry before the pop (cancel "back"). If the overlay's `close()` in turn
 * NAVIGATES (URL-addressable overlays that toggle a search-param with
 * `replace:true`, e.g. `?new=`/`?tx=`), that `replace` and `go(1)` compete over
 * the History API: `replace` lands on the WRONG entry (the previous one, already
 * popped) and `go(1)` restores the URL with the param still set → router-state and
 * the address bar become OUT OF SYNC (the overlay hides but the URL
 * keeps the param, and on reload/re-navigation the overlay BOUNCES open). By deferring
 * `close()` to a macrotask, it runs AFTER `go(1)` settles, so the closing
 * `replace` lands on the correct entry and URL+state end up consistent.
 * For local-signal overlays (without navigation), the defer is imperceptible.
 */

interface OverlayEntry {
  isOpen: () => boolean;
  close: () => void;
}

const stack: OverlayEntry[] = [];

/**
 * Pure, testable logic: given a navigation action, decide whether it should CLOSE the
 * overlay (and return `true` to BLOCK navigation) instead of navigating.
 * It only intercepts "BACK", and only if `entry` is the most recently opened overlay.
 */
export function resolveBackDismiss(
  action: string,
  entry: OverlayEntry,
  overlays: readonly OverlayEntry[] = stack,
): boolean {
  if (action !== "BACK") {
    return false;
  }
  const topmostOpen = overlays.findLast((candidate) => candidate.isOpen());
  if (topmostOpen === entry) {
    entry.close();
    return true;
  }
  return false;
}

/**
 * @param isOpen  reactive signal: `true` while the overlay is open.
 * @param close   closes the overlay (e.g. `() => onOpenChange(false)`).
 */
export function useBackDismiss(isOpen: () => boolean, close: () => void): void {
  // Without RouterProvider (isolated component tests / SSR) the blocker does not apply;
  // back-dismiss is progressive enhancement, so we no-op. In production
  // the overlay is always mounted inside the router.
  if (!useRouter({ warn: false })) {
    return;
  }

  // Closing is DEFERRED to a macrotask (see CC-19 header): it must run
  // after the `history.go(1)` the library executes when blocking BACK, so
  // a `close()` that navigates (URL-addressable overlays) does not compete with
  // that restoration and leave URL/state out of sync (an overlay that bounces back).
  const deferredClose = () => {
    setTimeout(close, 0);
  };
  const entry: OverlayEntry = { isOpen, close: deferredClose };
  stack.push(entry);
  onCleanup(() => {
    const idx = stack.indexOf(entry);
    if (idx !== -1) {
      stack.splice(idx, 1);
    }
  });

  // `enableBeforeUnload: false` → we do not want the native "Leave site?" prompt.
  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: ({ action }) => resolveBackDismiss(action, entry),
  });
}
